import {
    Box,
    Container,
    Divider,
    Grid,
    Link,
    Stack,
    Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import BrandLogo from '@/components/BrandLogo'
import { useBranding } from '@/contexts/BrandingContext'
import { isBetaMode as isBeta } from '@/lib/betaMode'
import { footerColumns } from '../footerLinks.js'
import { homeCopyFor } from '../homeCopy.js'

/**
 * The public site's footer. Written fresh rather than reviving `components/Footer.jsx`, the
 * template footer the blog layout still uses (NeighbourGood logo, placeholder links).
 *
 * Neutral surface and `primary` hovers only, so it sits under any partner's brand; the product
 * name comes from the resolved brand, never a hard-coded name.
 */
const MarketingFooter = () => {
    const { brand } = useBranding()
    const columns = footerColumns({
        isPartnerHost: Boolean(brand?.is_partner),
        isBeta,
        brandKey: brand?.brand_key
    })
    const productName = brand?.product_name || 'Power'

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                pb: 4,
                pt: { xs: 6, md: 8 },
                // viewport-fit=cover: keep content out of the notch on a landscape iPhone.
                pl: 'env(safe-area-inset-left, 0px)',
                pr: 'env(safe-area-inset-right, 0px)'
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box
                            component={RouterLink}
                            sx={{
                                color: 'text.primary',
                                display: 'inline-flex',
                                textDecoration: 'none'
                            }}
                            to="/"
                        >
                            <BrandLogo height={40} />
                        </Box>
                        <Typography
                            color="text.secondary"
                            sx={{ mt: 1.5, maxWidth: 280 }}
                            variant="body2"
                        >
                            {homeCopyFor(brand?.brand_key).footerTagline}
                        </Typography>
                    </Grid>
                    {columns.map((column) => (
                        <Grid key={column.title} size={{ xs: 6, sm: 4, md: 3 }}>
                            <Typography
                                color="text.primary"
                                component="h2"
                                sx={{ mb: 1.5 }}
                                variant="subtitle2"
                            >
                                {column.title}
                            </Typography>
                            <Stack
                                component="ul"
                                spacing={1}
                                sx={{ m: 0, p: 0 }}
                            >
                                {column.links.map((link) => (
                                    <Box
                                        component="li"
                                        key={link.to}
                                        sx={{ listStyle: 'none' }}
                                    >
                                        <Link
                                            color="text.secondary"
                                            component={RouterLink}
                                            sx={{
                                                '&:hover': {
                                                    color: 'primary.main'
                                                }
                                            }}
                                            to={link.to}
                                            underline="none"
                                            variant="body2"
                                        >
                                            {link.label}
                                        </Link>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
                <Divider sx={{ my: 4 }} />
                <Typography color="text.secondary" variant="caption">
                    {`© ${new Date().getFullYear()} ${productName}`}
                </Typography>
            </Container>
        </Box>
    )
}

export default MarketingFooter
